import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/tax-returns';

// ============================================
// Tax returns and whether they are paid, from imported bank statements
// ============================================

/**
 * @param {Object} params - { company, project, year, month, status, type }
 * @returns {Promise<Object>} { items, summary, statementCoverage }
 */
export const getTaxReturns = async (params) => {
  const response = await axiosClient.get(API_BASE, { params });
  return response.data;
};

export const getTaxReturn = async (company, id) => {
  const response = await axiosClient.get(`${API_BASE}/${id}`, { params: { company } });
  return response.data;
};

/** @param {'return'|'order'} [which] - the return's own file, or the payment order page of a PDPO return */
export const getTaxReturnFileUrl = async (company, id, which = 'return') => {
  const response = await axiosClient.get(`${API_BASE}/${id}/file`, { params: { company, which } });
  return response.data.url;
};

/**
 * @param {string} company
 * @param {File[]} files - notices and bank statements, in any mix
 * @param {Object} [options] - { project, kind }
 * @returns {Promise<Object>} { results, statuses }
 */
export const importTaxDocuments = async (company, files, options = {}) => {
  const form = new FormData();
  form.append('company', company);
  if (options.project) form.append('project', options.project);
  if (options.kind) form.append('kind', options.kind);
  if (options.category) form.append('category', options.category);
  form.append('sourcePaths', JSON.stringify(files.map((f) => f.webkitRelativePath || f.name)));
  files.forEach((file) => form.append('files', file));

  const response = await axiosClient.post(`${API_BASE}/import`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 10 * 60 * 1000,
  });
  return response.data;
};

export const updateTaxReturn = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/${id}`, { company, ...data });
  return response.data;
};

/** `status: null` hands the return back to the automatic matching. */
export const overrideTaxReturnStatus = async (company, id, { status, reason }) => {
  const response = await axiosClient.patch(`${API_BASE}/${id}/status`, { company, status, reason });
  return response.data;
};

export const deleteTaxReturn = async (company, id) => {
  await axiosClient.delete(`${API_BASE}/${id}`, { params: { company } });
};

export const getBankAccounts = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/bank-accounts`, { params: { company } });
  return response.data.items;
};

export const setBankAccountProject = async (company, account, project) => {
  const response = await axiosClient.patch(`${API_BASE}/bank-accounts/${account}`, { company, project });
  return response.data.items;
};

/**
 * @param {string} company
 * @param {Object} filters - the list filters
 * @param {Object} labels - column and status names in the viewer's language
 * @returns {Promise<Blob>}
 */
export const exportTaxReturns = async (company, filters, labels) => {
  const response = await axiosClient.post(
    `${API_BASE}/export`,
    { company, filters, labels },
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Accounts and folder offered for the next AB journal: the last used ones, else the defaults.
 * With `ids`, also the recipients of those returns and the AB partner code each one matches.
 */
export const getAbSettings = async (company, ids = []) => {
  const params = ids.length ? { company, ids: ids.join(',') } : { company };
  const response = await axiosClient.get(`${API_BASE}/ab-settings`, { params });
  return response.data;
};

/**
 * Journal file for AB Soft booking the chosen PDPO returns.
 *
 * @param {Object} data - { company, ids, folder, date (YYYY-MM-DD), accounts, partners: { name: code } }
 * @returns {Promise<{ blob: Blob, count: number, warnings: string[] }>}
 */
export const exportAbJournal = async (data) => {
  let response;
  try {
    response = await axiosClient.post(`${API_BASE}/ab-journal`, data, { responseType: 'blob' });
  } catch (error) {
    // With a blob response the error body is a blob too; read it so the message key reaches the page.
    if (error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        /* keep the original error */
      }
    }
    throw error;
  }
  let warnings = [];
  try {
    warnings = JSON.parse(decodeURIComponent(response.headers['x-journal-warnings'] || '%5B%5D'));
  } catch {
    warnings = [];
  }
  return { blob: response.data, count: Number(response.headers['x-journal-count']) || 0, warnings };
};

// ============================================
// Withholding tax calculations (rent, other income)
// ============================================

/** @param {Object} params - { company, project, year, month, kind, status } */
export const getCalculations = async (params) => {
  const response = await axiosClient.get(`${API_BASE}/calculations`, { params });
  return response.data;
};

export const getCalculation = async (company, id) => {
  const response = await axiosClient.get(`${API_BASE}/calculations/${id}`, { params: { company } });
  return response.data;
};

export const createCalculation = async (company, data) => {
  const response = await axiosClient.post(`${API_BASE}/calculations`, { company, ...data });
  return response.data;
};

export const updateCalculation = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/calculations/${id}`, { company, ...data });
  return response.data;
};

/** Done with the return's BOP and UID, or `done: false` to reopen it. */
export const setCalculationDone = async (company, id, data) => {
  const response = await axiosClient.patch(`${API_BASE}/calculations/${id}/done`, { company, ...data });
  return response.data;
};

export const deleteCalculation = async (company, id) => {
  await axiosClient.delete(`${API_BASE}/calculations/${id}`, { params: { company } });
};

export const exportCalculationOrders = async (company, filters, labels) => {
  const response = await axiosClient.post(
    `${API_BASE}/calculations/export`,
    { company, filters, labels },
    { responseType: 'blob' }
  );
  return response.data;
};

/** NBS middle rate; `rate` is null when the service has none for the day. */
export const getExchangeRate = async (currency, date) => {
  const response = await axiosClient.get(`${API_BASE}/exchange-rate`, { params: { currency, date } });
  return response.data;
};

// ============================================
// Google Drive as a source of notices and statements
// ============================================

export const getDriveStatus = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/drive`, { params: { company } });
  return response.data;
};

/** Google consent address; the page sends the browser there. */
export const getDriveConnectUrl = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/drive/connect`, { params: { company } });
  return response.data.url;
};

/** Folders shared with the connected account, or the subfolders of `parent`. */
export const getDriveFolders = async (company, parent) => {
  const response = await axiosClient.get(`${API_BASE}/drive/folders`, { params: { company, parent } });
  return response.data;
};

export const updateDriveSettings = async (company, data) => {
  const response = await axiosClient.patch(`${API_BASE}/drive`, { company, ...data });
  return response.data;
};

export const startDriveSync = async (company) => {
  const response = await axiosClient.post(`${API_BASE}/drive/sync`, { company });
  return response.data;
};

export const disconnectDrive = async (company) => {
  const response = await axiosClient.delete(`${API_BASE}/drive`, { params: { company } });
  return response.data;
};

// ============================================
// A network folder of statements, sent by the AB Soft workstation
// ============================================

export const getShareStatus = async (company) => {
  const response = await axiosClient.get(`${API_BASE}/share`, { params: { company } });
  return response.data;
};

export const updateShareSettings = async (company, path) => {
  const response = await axiosClient.patch(`${API_BASE}/share`, { company, path });
  return response.data;
};
